import { styled } from '@mui/material/styles';
import { Box, Button, IconButton, Paper, Drawer, Typography } from '@mui/material';

// Reusable scrollbar styles that can be applied to any component
export const scrollbarStyles = (theme) => ({
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(44, 62, 80, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.background.light,
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.background.dark,
    },
  },
});

// Sidebar Components
export const VerticalSidebar = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  left: 0,
  top: 0,
  bottom: 0,
  width: '56px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '16px',
  gap: '8px',
  zIndex: 1300,
  borderRadius: 0,
  backgroundColor: theme.palette.background.main,
  border: 'none',
  '&::before, &::after': {
    display: 'none',
  },
}));

export const SidebarIcon = styled(IconButton)(({ theme, active }) => ({
  color: active ? theme.palette.secondary.main : theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

export const SecondaryModeButton = styled(IconButton)(({ theme, active }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '6px',
  backgroundColor: active 
    ? theme.palette.primary.main 
    : 'rgba(140, 155, 181, 0.05)',
  color: active 
    ? theme.palette.background.main 
    : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: active 
      ? theme.palette.primary.light 
      : 'rgba(140, 155, 181, 0.25)',
    color: active 
      ? theme.palette.background.main 
      : theme.palette.text.primary,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
}));

export const ModeButton = styled(IconButton)(({ theme, active }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '6px',
  backgroundColor: active 
    ? theme.palette.secondary.main 
    : 'rgba(140, 155, 181, 0.15)',
  color: active 
    ? theme.palette.background.main 
    : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: active 
      ? theme.palette.secondary.light 
      : 'rgba(140, 155, 181, 0.25)',
    color: active 
      ? theme.palette.background.main 
      : theme.palette.text.primary,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
}));

export const DangerIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
    color: theme.palette.error.light,
  },
}));

// Toolbar Components
export const ContextualToolbar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 'var(--game-sidebar-content-left, 56px)',
  right: 0,
  height: '60px',
  backgroundColor: theme.palette.background.light,
  borderBottom: `1px solid ${theme.palette.background.dark}`,
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  gap: '12px',
  zIndex: 1200,
}));

export const ToolbarButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '6px 12px',
  fontSize: '0.8rem',
  fontFamily: 'MedievalSharp',
}));

export const PaintSwatch = styled(Box)(({ theme, selected, image }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '4px',
  cursor: 'pointer',
  backgroundImage: image ? `url(${image})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: selected ? `3px solid ${theme.palette.secondary.main}` : '2px solid transparent',
  boxShadow: selected ? `0 0 0 2px ${theme.palette.secondary.main}40` : 'none',
  transition: 'all 0.15s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    borderColor: theme.palette.secondary.main,
  },
}));

export const ToolbarDivider = styled(Box)(({ theme }) => ({
  width: '2px',
  height: '32px',
  opacity: 0.3,
  backgroundColor: theme.palette.text.secondary,
  margin: '0 8px',
}));

export const SelectionBox = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 8px',
  borderRadius: '6px',
  cursor: 'pointer',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '2px solid',
  borderColor: active ? theme.palette.secondary.main : 'rgba(255, 255, 255, 0.2)',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: theme.palette.secondary.main,
  }
}));

export const SelectionPreview = styled(Box)(() => ({
  width: '28px',
  height: '28px',
  borderRadius: '4px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: '2px solid transparent',
  flexShrink: 0,
}));

export const SelectionText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather, serif',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

export const SelectionMenuItem = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  borderRadius: '6px',
  cursor: 'pointer',
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  border: '2px solid',
  borderColor: selected ? theme.palette.secondary.main : 'rgba(255, 255, 255, 0.1)',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.secondary.light,
  }
}));

export const SelectionMenuPreview = styled(Box)(() => ({
  width: '32px',
  height: '32px',
  borderRadius: '4px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  flexShrink: 0,
}));

export const SelectionMenuText = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '0.9rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

// Drawer Components
export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    border: 'none',
    boxShadow: 'none',
    marginLeft: '56px',
  }
}));

export const DrawerContent = styled(Paper)(({ theme }) => ({
  width: 600,
  height: '100%',
  padding: 0,
  backgroundColor: theme.palette.background.light,
  border: `3px solid ${theme.palette.background.dark}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.15,
    mixBlendMode: 'multiply',
    pointerEvents: 'none',
  }
}));

export const MenuHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.light,
  borderBottom: `2px solid ${theme.palette.background.dark}`,
  padding: '16px 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& .MuiTypography-root': {
    margin: 0,
    color: theme.palette.text.primary,
    fontFamily: 'MedievalSharp',
    fontWeight: 700,
    fontSize: '1.4rem',
    lineHeight: 1.6,
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    letterSpacing: '0.05em',
    textTransform: 'none'
  },
  '& .MuiIconButton-root': {
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(218, 165, 32, 0.1)'
    }
  }
}));

export const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

// Badge Components
export const CreatorBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(52, 73, 94, 0.9) 0%, rgba(44, 62, 80, 0.9) 100%)',
  border: `1px solid ${theme.palette.secondary.main}`,
  marginLeft: 'auto',
  height: '28px',
  '& .MuiSvgIcon-root': {
    color: '#FFD700',
    filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.25))',
    animation: 'sparkle 1.5s infinite',
    fontSize: '1.2rem',
  },
  '& .MuiTypography-root': {
    color: theme.palette.secondary.main,
    fontFamily: 'MedievalSharp',
    fontSize: '0.85rem',
    fontWeight: 600,
    textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)',
    lineHeight: 1,
  },
  '@keyframes sparkle': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.7 }
  }
}));

export const ActiveMapBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(52, 73, 94, 0.9) 0%, rgba(44, 62, 80, 0.9) 100%)',
  border: `1px solid ${theme.palette.secondary.main}`,
  marginLeft: theme.spacing(1),
  height: '24px',
  '& .MuiSvgIcon-root': {
    color: '#FFD700',
    fontSize: '1rem',
  },
  '& .MuiTypography-root': {
    color: theme.palette.secondary.main,
    fontFamily: 'MedievalSharp',
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: 1,
  },
}));

export const PresenterBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(41, 128, 185, 0.9) 0%, rgba(52, 73, 94, 0.9) 100%)',
  border: `1px solid ${theme.palette.info.main}`,
  marginLeft: 'auto',
  height: '28px',
  '& .MuiSvgIcon-root': {
    color: '#5DADE2',
    filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.25))',
    fontSize: '1.2rem',
  },
  '& .MuiTypography-root': {
    color: theme.palette.info.light,
    fontFamily: 'MedievalSharp',
    fontSize: '0.85rem',
    fontWeight: 600,
    textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)',
    lineHeight: 1,
  },
}));

// Button Components
export const LeaveRoomButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderColor: theme.palette.error.main,
  color: theme.palette.error.main,
  '&:hover': {
    borderColor: theme.palette.error.dark,
    backgroundColor: 'rgba(211, 47, 47, 0.04)',
  },
}));

// App Layout Components
export const MedievalBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: 0,
  paddingLeft: '56px',
  paddingTop: '60px',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
}));

export const ContentWrapper = styled(Box)(() => ({
  display: 'flex',
  height: 'calc(100vh - 60px)',
  width: '100%',
}));

export const FixedPanel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: '56px',
  top: 0,
  bottom: 0,
  width: '280px',
  height: '100vh',
  backgroundColor: theme.palette.background.light,
  borderRight: `2px solid ${theme.palette.background.dark}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1100,
}));

export const GameContainer = styled(Box)(() => ({
  position: 'relative',
  padding: 0,
  flex: 1,
  height: '100%',
  margin: 0,
  zIndex: 1,
  overflow: 'auto',
  '& .grid-cell': {
    backgroundColor: 'white',
    backgroundImage: 'none',
  },
  '& .grid-container': {
    backgroundColor: 'white',
    backgroundImage: 'none',
  },
}));

export const Watermark = styled(Typography)(() => ({
  position: 'fixed',
  bottom: '12px',
  right: '12px',
  zIndex: 1000,
  userSelect: 'none',
  opacity: 0.5,
}));

export const MapContextBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 16px',
  marginBottom: '8px',
  backgroundColor: 'rgba(44, 62, 80, 0.2)',
  borderRadius: theme.shape.borderRadius,
  gap: '16px',
}));

export const ActivateButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 14px',
  borderRadius: '16px',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  cursor: 'pointer',
  fontFamily: 'MedievalSharp',
  fontSize: '0.85rem',
  fontWeight: 600,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.02)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
  },
}));

export const ZoomButton = styled(Box)(({ theme, zoom, disabled}) => ({
  width: '36px',
  height: '36px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: disabled ? '#555' : theme.palette.primary.dark,
  color: 'white',
  fontSize: '18px',
  fontWeight: 'bold',
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: disabled ? '#555' : theme.palette.primary.light,
  },
}));

export const TwoColumnLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  height: 650,
}));

export const LeftColumn = styled(Box)(({ theme }) => ({
  flex: '0 0 320px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

export const RightColumn = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
}));